import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  CHAPTERS, CHAPTER_OVI_COUNTS, DNYANKIRAN_CATEGORIES, validateDnyankiranCard,
} from '@dnyaneshwari/shared';
import type { DnyankiranCard } from '@dnyaneshwari/shared';
import {
  listCards, saveCard, scheduleCard, unscheduleCard, deleteCard, publishNow, uploadAsset, fetchOviText,
  nextNumber, getSettings, setSettings, type CardDraft, type AssetKind,
} from '@/services/dnyankiranService';

const EMPTY: CardDraft = {
  number: 1,
  category: 'khagol',
  title: { mr: '', en: '' },
  ovi: { chapterId: '04', oviNumber: 99, textMarathi: '' },
  oviEnglish: '',
  explanation: { mr: '', en: '' },
  teaching: { mr: '', en: '' },
  illustrationUrl: undefined,
  shareImageUrl: {},
  videoUrl: {},
};

const STATUS_STYLE: Record<DnyankiranCard['status'], string> = {
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-blue-100 text-blue-800',
  published: 'bg-green-100 text-green-800',
};

const catLabel = (id: string) => DNYANKIRAN_CATEGORIES.find((c) => c.id === id)?.mr ?? id;

/**
 * ज्ञानकिरण: write the cards, pick the ovi from the book, attach the
 * designer's files, and decide when each goes out. Only "Publish now" and the
 * scheduler put a card in the app, and both do it through the function.
 */
export default function DnyankiranPage() {
  const [cards, setCards] = useState<DnyankiranCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ id: string | null; draft: CardDraft; status: DnyankiranCard['status']; pushSent: boolean } | null>(null);
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [sendTime, setSendTime] = useState('07:00');
  const [timeDraft, setTimeDraft] = useState('07:00');
  const [scheduleDate, setScheduleDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [scheduleTime, setScheduleTime] = useState('07:00');
  // Off until the app that can open the card is live: the Android app in the
  // store today has no ज्ञानकिरण screen, so a push would reach it with
  // nowhere to go. Flip the default to true with the v2 release.
  const [sendPush, setSendPush] = useState(false);

  const [loadError, setLoadError] = useState('');
  async function refresh() {
    setLoading(true);
    try {
      setCards(await listCards());
      setLoadError('');
    } catch (err) {
      // Before the rules are deployed this is "Missing or insufficient
      // permissions" -- say so, rather than showing an empty list that looks
      // like there are simply no cards.
      setLoadError((err as Error).message);
    } finally { setLoading(false); }
  }
  useEffect(() => { void refresh(); }, []);
  useEffect(() => {
    getSettings().then((s) => { setSendTime(s.sendTime); setTimeDraft(s.sendTime); setScheduleTime(s.sendTime); });
  }, []);

  const live = cards.find((c) => c.status === 'published' && c.publishedAt)
    ? [...cards].filter((c) => c.status === 'published').sort((a, b) => (b.publishedAt?.getTime() ?? 0) - (a.publishedAt?.getTime() ?? 0))[0]
    : null;

  const problems = useMemo(() => (editing ? validateDnyankiranCard(editing.draft) : []), [editing]);

  function edit(card: DnyankiranCard | null) {
    if (card) {
      const { id, status: st, pushSent, publishAt: _p, publishedAt: _q, createdAt: _c, updatedAt: _u, ...rest } = card;
      setEditing({ id, draft: { ...EMPTY, ...rest }, status: st, pushSent });
    } else {
      void nextNumber().then((n) => setEditing({ id: null, draft: { ...EMPTY, number: n }, status: 'draft', pushSent: false }));
    }
    setStatus('');
  }

  const set = (fn: (d: CardDraft) => void) =>
    setEditing((e) => { if (!e) return e; const draft = structuredClone(e.draft); fn(draft); return { ...e, draft }; });

  async function loadOvi() {
    if (!editing) return;
    const { chapterId, oviNumber } = editing.draft.ovi;
    const max = CHAPTER_OVI_COUNTS[parseInt(chapterId, 10) - 1] ?? 0;
    if (!(oviNumber >= 1 && oviNumber <= max)) { setStatus(`Chapter ${parseInt(chapterId, 10)} has ovis 1–${max}.`); return; }
    setBusy('ovi');
    try {
      const ovi = await fetchOviText(chapterId, oviNumber);
      if (!ovi) { setStatus('That ovi is not in the published bundle.'); return; }
      set((d) => { d.ovi.textMarathi = ovi.textMarathi; });
      setStatus('');
    } finally { setBusy(''); }
  }

  async function save(): Promise<string | null> {
    if (!editing) return null;
    setBusy('save');
    try {
      const id = await saveCard(editing.id, editing.draft);
      setEditing((e) => (e ? { ...e, id } : e));
      await refresh();
      setStatus('Saved.');
      return id;
    } catch (err) {
      setStatus((err as Error).message);
      return null;
    } finally { setBusy(''); }
  }

  async function upload(kind: AssetKind, file: File) {
    if (!editing) return;
    let id = editing.id;
    if (!id) { id = await save(); if (!id) return; }
    setBusy(kind);
    try {
      const url = await uploadAsset(id, kind, file, (pct) => setProgress((p) => ({ ...p, [kind]: pct })));
      set((d) => {
        if (kind === 'illustration') d.illustrationUrl = url;
        else if (kind === 'video-mr' || kind === 'video-en') d.videoUrl = { ...(d.videoUrl ?? {}), [kind === 'video-mr' ? 'mr' : 'en']: url };
        else d.shareImageUrl = { ...(d.shareImageUrl ?? {}), [kind === 'share-mr' ? 'mr' : 'en']: url };
      });
      // Persist the URL at once so a reload does not lose the upload.
      const draft = structuredClone(editing.draft);
      if (kind === 'illustration') draft.illustrationUrl = url;
      else if (kind === 'video-mr' || kind === 'video-en') draft.videoUrl = { ...(draft.videoUrl ?? {}), [kind === 'video-mr' ? 'mr' : 'en']: url };
      else draft.shareImageUrl = { ...(draft.shareImageUrl ?? {}), [kind === 'share-mr' ? 'mr' : 'en']: url };
      await saveCard(id, draft);
      await refresh();
    } catch (err) {
      setStatus((err as Error).message);
    } finally { setBusy(''); setProgress((p) => ({ ...p, [kind]: 0 })); }
  }

  async function doSchedule() {
    if (!editing || problems.length) return;
    const id = await save();
    if (!id) return;
    setBusy('schedule');
    try {
      await scheduleCard(id, new Date(`${scheduleDate}T${scheduleTime}:00+05:30`), sendPush);
      await refresh();
      setEditing((e) => (e ? { ...e, status: 'scheduled' } : e));
      setStatus(`Scheduled for ${scheduleDate} ${scheduleTime} IST${sendPush ? ', with a push' : ', without a push'}.`);
    } catch (err) { setStatus((err as Error).message); } finally { setBusy(''); }
  }

  async function doPublish(resend = false) {
    if (!editing || problems.length) return;
    const id = resend ? editing.id : await save();
    if (!id) return;
    const push = resend || sendPush;
    if (!window.confirm(resend ? 'Send the push again to everyone?' : push ? 'Publish this card to every phone now, with a push notification?' : 'Publish this card now, without a notification?')) return;
    setBusy('publish');
    try {
      const { pushed } = await publishNow(id, resend, push);
      await refresh();
      setEditing((e) => (e ? { ...e, status: 'published', pushSent: e.pushSent || pushed } : e));
      setStatus(pushed ? 'Published and push sent.' : push ? 'Published. Push was already sent earlier — use "Send push again" if you mean to repeat it.' : 'Published, no push sent.');
    } catch (err) { setStatus((err as Error).message); } finally { setBusy(''); }
  }

  async function doUnschedule() {
    if (!editing?.id) return;
    setBusy('unschedule');
    try { await unscheduleCard(editing.id); await refresh(); setEditing((e) => (e ? { ...e, status: 'draft' } : e)); setStatus('Back to draft.'); }
    finally { setBusy(''); }
  }

  async function doDelete() {
    if (!editing?.id || editing.status !== 'draft') return;
    if (!window.confirm('Delete this draft?')) return;
    await deleteCard(editing.id);
    setEditing(null);
    await refresh();
  }

  async function saveTime() {
    await setSettings({ sendTime: timeDraft });
    setSendTime(timeDraft);
    setScheduleTime(timeDraft);
  }

  const input = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-amber-400';
  const label = 'block text-xs font-medium text-gray-600 mb-1';
  const btn = 'px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50';

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ज्ञानकिरण</h1>
          <p className="text-sm text-gray-500 mt-1">One ovi, one modern truth. The app shows exactly one card: the last one published.</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 text-sm">
          <span className="text-gray-700"><strong>Send time</strong> · IST · now {sendTime}</span>
          <input type="time" step={900} value={timeDraft} onChange={(e) => setTimeDraft(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
          <button onClick={saveTime} disabled={timeDraft === sendTime} className={`${btn} bg-amber-600 text-white`}>Save</button>
        </div>
      </div>

      {live && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-900">
          <strong>Live in the app:</strong> क्र. {live.number} · {catLabel(live.category)} · {live.title.mr}
          {live.publishedAt && <span className="text-green-700"> · since {format(live.publishedAt, 'd MMM HH:mm')}</span>}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <section className="bg-white rounded-xl border border-gray-200 overflow-hidden self-start">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span className="font-semibold text-gray-900">Cards</span>
            <button onClick={() => edit(null)} className={`${btn} bg-amber-600 text-white flex items-center gap-1`}><PlusIcon className="w-4 h-4" />New card</button>
          </div>
          {loading ? (
            <p className="px-4 py-10 text-center text-sm text-gray-400">Loading…</p>
          ) : loadError ? (
            <p className="px-4 py-8 text-center text-sm text-red-700">Could not load the cards: {loadError}<br /><span className="text-gray-500">If this says permissions, the ज्ञानकिरण rules and functions have not been deployed yet.</span></p>
          ) : cards.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-gray-400">No cards yet. Make the first one.</p>
          ) : (
            <ul>
              {cards.map((c) => (
                <li key={c.id}>
                  <button onClick={() => edit(c)} className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-amber-50 flex items-center gap-3 ${editing?.id === c.id ? 'bg-amber-50' : ''}`}>
                    <span className="w-9 h-9 rounded-full bg-amber-900 text-amber-200 flex items-center justify-center text-sm font-bold flex-shrink-0">{c.number}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs font-semibold text-amber-700">{catLabel(c.category)}</span>
                      <span className="block text-sm text-gray-900 truncate">{c.title.mr || <em className="text-gray-400">untitled</em>}</span>
                      <span className="block text-xs text-gray-500">अध्याय {parseInt(c.ovi.chapterId, 10)} · ओवी {c.ovi.oviNumber}{c.publishAt && c.status === 'scheduled' ? ` · ${format(c.publishAt, 'd MMM HH:mm')}` : ''}</span>
                    </span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[c.status]}`}>{c.status}</span>
                    {c.pushSent && <span title="Push sent" className="text-green-600 font-bold">✓</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {editing && (
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-4 self-start">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">{editing.id ? `Edit card · क्र. ${editing.draft.number}` : 'New card'}</h2>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[editing.status]}`}>{editing.status}</span>
                <button onClick={() => setEditing(null)} className="p-1 rounded-lg hover:bg-gray-100" aria-label="Close"><XMarkIcon className="w-5 h-5 text-gray-500" /></button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div><label className={label}>क्र.</label><input type="number" min={1} value={editing.draft.number} onChange={(e) => set((d) => { d.number = parseInt(e.target.value, 10) || 0; })} className={input} /></div>
              <div><label className={label}>Category</label>
                <select value={editing.draft.category} onChange={(e) => set((d) => { d.category = e.target.value; })} className={input}>
                  {DNYANKIRAN_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.mr} · {c.en}</option>)}
                </select></div>
              <div><label className={label}>Chapter</label>
                <select value={editing.draft.ovi.chapterId} onChange={(e) => set((d) => { d.ovi.chapterId = e.target.value; d.ovi.textMarathi = ''; })} className={input}>
                  {CHAPTERS.map((ch) => <option key={ch.number} value={String(ch.number).padStart(2, '0')}>{ch.number}. {ch.titleMarathi}</option>)}
                </select></div>
              <div><label className={label}>Ovi</label><input type="number" min={1} value={editing.draft.ovi.oviNumber} onChange={(e) => set((d) => { d.ovi.oviNumber = parseInt(e.target.value, 10) || 0; d.ovi.textMarathi = ''; })} onBlur={loadOvi} className={input} /></div>
            </div>

            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-stone-500">Verse, from the published book. This is what the card shows.</span>
                <button onClick={loadOvi} disabled={busy === 'ovi'} className="text-xs font-semibold text-amber-700">{busy === 'ovi' ? 'Loading…' : 'Load from book'}</button>
              </div>
              <textarea value={editing.draft.ovi.textMarathi} onChange={(e) => set((d) => { d.ovi.textMarathi = e.target.value; })} rows={4} className={`${input} font-serif leading-relaxed`} placeholder="Press “Load from book”, or paste the four lines." />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Title · मराठी</label><input value={editing.draft.title.mr} onChange={(e) => set((d) => { d.title.mr = e.target.value; })} className={input} /></div>
              <div><label className={label}>Title · English</label><input value={editing.draft.title.en ?? ''} onChange={(e) => set((d) => { d.title.en = e.target.value; })} className={input} /></div>
              <div className="col-span-2"><label className={label}>Ovi in English (one line, English card only)</label><input value={editing.draft.oviEnglish ?? ''} onChange={(e) => set((d) => { d.oviEnglish = e.target.value; })} className={input} /></div>
              <div><label className={label}>Explanation · मराठी</label><textarea rows={4} value={editing.draft.explanation.mr} onChange={(e) => set((d) => { d.explanation.mr = e.target.value; })} className={input} /></div>
              <div><label className={label}>Explanation · English</label><textarea rows={4} value={editing.draft.explanation.en ?? ''} onChange={(e) => set((d) => { d.explanation.en = e.target.value; })} className={input} /></div>
              <div><label className={label}>माऊलींची शिकवण · मराठी</label><textarea rows={3} value={editing.draft.teaching.mr} onChange={(e) => set((d) => { d.teaching.mr = e.target.value; })} className={input} /></div>
              <div><label className={label}>Mauli’s teaching · English</label><textarea rows={3} value={editing.draft.teaching.en ?? ''} onChange={(e) => set((d) => { d.teaching.en = e.target.value; })} className={input} /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {([
                ['illustration', 'Card picture · 2:1 (the app shows this)', editing.draft.illustrationUrl, 'image/*'],
                ['share-mr', 'Share card · मराठी (JPEG)', editing.draft.shareImageUrl?.mr, 'image/*'],
                ['share-en', 'Share card · English (JPEG)', editing.draft.shareImageUrl?.en, 'image/*'],
                ['video-mr', 'Animation · मराठी (MP4)', editing.draft.videoUrl?.mr, 'video/mp4'],
                ['video-en', 'Animation · English (MP4)', editing.draft.videoUrl?.en, 'video/mp4'],
              ] as [AssetKind, string, string | undefined, string][]).map(([kind, text, url, accept]) => (
                <div key={kind} className="border border-dashed border-gray-300 rounded-xl p-3 text-sm">
                  <div className="font-medium text-gray-700 mb-1">{text}</div>
                  {url ? (
                    kind.startsWith('video')
                      ? <a href={url} target="_blank" rel="noreferrer" className="text-xs text-green-700 font-semibold">uploaded ✓ · open</a>
                      : <img src={url} alt="" className="h-20 rounded-md object-cover mb-1" />
                  ) : <div className="text-xs text-gray-400 mb-1">none yet</div>}
                  <input type="file" accept={accept} disabled={!!busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(kind, f); e.target.value = ''; }} className="text-xs" />
                  {progress[kind] ? <div className="text-xs text-amber-700 mt-1">{progress[kind]}%</div> : null}
                </div>
              ))}
            </div>
            {!editing.id && <p className="text-xs text-gray-500">Files can be attached after the first save.</p>}

            {problems.length > 0 && (
              <ul className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 space-y-0.5">
                <li className="font-semibold">Before this card can go out:</li>
                {problems.map((p) => <li key={p}>· {p}</li>)}
              </ul>
            )}
            {status && <p className="text-sm text-gray-700">{status}</p>}

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={sendPush} onChange={(e) => setSendPush(e.target.checked)} />
              Send a push notification when this card goes live
              <span className="text-xs text-gray-500">— keep off until the app with the ज्ञानकिरण screen is in the store</span>
            </label>
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
              <button onClick={() => void save()} disabled={!!busy} className={`${btn} border border-gray-200 text-gray-700`}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
              {editing.status !== 'published' && (
                <>
                  <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
                  <input type="time" step={900} value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
                  <button onClick={doSchedule} disabled={!!busy || problems.length > 0} className={`${btn} bg-blue-600 text-white`}>{editing.status === 'scheduled' ? 'Reschedule' : 'Schedule'}</button>
                </>
              )}
              {editing.status === 'scheduled' && <button onClick={doUnschedule} disabled={!!busy} className={`${btn} border border-gray-200 text-gray-700`}>Unschedule</button>}
              <span className="flex-1" />
              {editing.status === 'published' && editing.pushSent && (
                <button onClick={() => doPublish(true)} disabled={!!busy} className={`${btn} border border-amber-300 text-amber-800`}>Send push again</button>
              )}
              <button onClick={() => doPublish(false)} disabled={!!busy || problems.length > 0} className={`${btn} bg-amber-600 text-white`}>{busy === 'publish' ? 'Publishing…' : editing.status === 'published' ? 'Publish again (update live card)' : 'Publish now'}</button>
              {editing.id && editing.status === 'draft' && <button onClick={doDelete} disabled={!!busy} className={`${btn} text-red-600`}>Delete</button>}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
